import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { getTranslation } from '../data/translations';
import { whatsappConfig } from '../data/mockData';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { MessageCircle, MapPin, Mail, Clock, Send } from 'lucide-react';
import { toast } from 'sonner';

const Contact = () => {
  const { language } = useLanguage();
  const t = (key) => getTranslation(language, key);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    business: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/contact/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success(data.message || t('contact.success'));
        setFormData({
          name: '',
          email: '',
          phone: '',
          business: '',
          message: ''
        });
      } else {
        toast.error(data.message || t('contact.error'));
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error(t('contact.error'));
    }
  };

  const handleWhatsApp = () => {
    const message = encodeURIComponent(whatsappConfig.defaultMessage[language]);
    const whatsappUrl = `https://wa.me/${whatsappConfig.number}?text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-[#1a3a52] to-[#2a4a62]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            {t('contact.title')}
          </h1>
          <p className="text-xl text-gray-200">
            {t('contact.subtitle')}
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border-none shadow-2xl bg-white">
                <CardContent className="p-8 md:p-12">
                  <h2 className="text-3xl font-bold text-[#1a3a52] mb-8">
                    {t('contact.formTitle')}
                  </h2>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-[#1a3a52] font-semibold">
                          {t('contact.name')}
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          placeholder={t('contact.namePlaceholder')}
                          className="border-gray-300 focus:border-[#1a3a52] focus:ring-[#1a3a52]"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-[#1a3a52] font-semibold">
                          {t('contact.email')}
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          placeholder={t('contact.emailPlaceholder')}
                          className="border-gray-300 focus:border-[#1a3a52] focus:ring-[#1a3a52]"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-[#1a3a52] font-semibold">
                          {t('contact.phone')}
                        </Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          required
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder={t('contact.phonePlaceholder')}
                          className="border-gray-300 focus:border-[#1a3a52] focus:ring-[#1a3a52]"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="business" className="text-[#1a3a52] font-semibold">
                          {t('contact.business')} *
                        </Label>
                        <Input
                          id="business"
                          name="business"
                          type="text"
                          required
                          value={formData.business}
                          onChange={handleChange}
                          placeholder={t('contact.businessPlaceholder')}
                          className="border-gray-300 focus:border-[#1a3a52] focus:ring-[#1a3a52]"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-[#1a3a52] font-semibold">
                        {t('contact.message')}
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        required
                        value={formData.message}
                        onChange={handleChange}
                        placeholder={t('contact.messagePlaceholder')}
                        rows={6}
                        className="border-gray-300 focus:border-[#1a3a52] focus:ring-[#1a3a52] resize-none"
                      />
                    </div>
                    <Button
                      type="submit"
                      size="lg"
                      className="w-full bg-[#1a3a52] hover:bg-[#0f2535] text-white py-6 text-lg font-semibold transition-all duration-300 hover:scale-105"
                    >
                      <Send className="h-5 w-5 mr-2" />
                      {t('contact.submit')}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Contact Info */}
            <div className="space-y-6">
              {/* Contact Info Card */}
              <Card className="border-none shadow-xl bg-white">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-[#1a3a52] mb-6">
                    {t('contact.infoTitle')}
                  </h3>
                  <div className="space-y-6">
                    <div className="flex items-start space-x-4">
                      <div className="bg-[#c5a365]/10 rounded-full p-3 flex-shrink-0">
                        <Mail className="h-6 w-6 text-[#c5a365]" />
                      </div>
                      <div>
                        <p className="font-semibold text-[#1a3a52] mb-1">
                          {language === 'en' ? 'Email' : 'البريد الإلكتروني'}
                        </p>
                        <a href="mailto:sales@tajgulfoman.com" className="text-[#c5a365] hover:underline">
                          sales@tajgulfoman.com
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start space-x-4">
                      <div className="bg-[#c5a365]/10 rounded-full p-3 flex-shrink-0">
                        <MapPin className="h-6 w-6 text-[#c5a365]" />
                      </div>
                      <div>
                        <p className="font-semibold text-[#1a3a52] mb-1">
                          {language === 'en' ? 'Location' : 'موقع'}
                        </p>
                        <p className="text-gray-600">Ruwi, Sultanate of Oman</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-4">
                      <div className="bg-[#c5a365]/10 rounded-full p-3 flex-shrink-0">
                        <Clock className="h-6 w-6 text-[#c5a365]" />
                      </div>
                      <div>
                        <p className="font-semibold text-[#1a3a52] mb-1">
                          {t('contact.hours')}
                        </p>
                        <p className="text-gray-600">{t('contact.hoursText')}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Stats Card */}
              <Card className="border-none shadow-xl bg-gradient-to-br from-[#1a3a52] to-[#2a4a62]">
                <CardContent className="p-8 text-white">
                  <h3 className="text-xl font-bold mb-6">
                    {language === 'en' ? 'Trusted by Many' : 'موثوق به من قبل الكثيرين'}
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span>{t('home.customersLabel')}</span>
                      <span className="text-2xl font-bold text-[#c5a365]">200+</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>{t('home.productsLabel')}</span>
                      <span className="text-2xl font-bold text-[#c5a365]">1000+</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>{t('home.experienceLabel')}</span>
                      <span className="text-2xl font-bold text-[#c5a365]">30+</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
